import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaChevronRight } from "react-icons/fa";
import BottomSheet from "./BottomSheet";
import LanguageSheet, { languagePickerAvailable } from "./LanguageSheet";
import { formatAmountForCopy } from "../utils/currency";
import { t } from "../i18n";
import "./FanPageMenu.css";

// The "⋯" menu on the fan's request page, top-right of the card, where the
// paper-plane share pill used to be (founder, 1.4.1). Three rows (four once a
// second language is offered: Language opens the shared picker), each one
// landing somewhere real:
//   Share DJ profile   → the page's own share action (native sheet / copy)
//   How requests work  → a three-line sheet, drunk-proof
//   Help & support     → the same chooser the DJ side has (MorePage):
//                        Share feedback / Terms / Contact us. Fans are
//                        signed out, so feedback and contact both open the
//                        mail app (POST /feedback needs a DJ login); Terms
//                        opens the two legal pages. The in-app chatbot is
//                        the DJ's and is deliberately hidden on fan pages.
// No Apple emojis: every icon is an inline SVG. The chooser rows reuse the
// Profile drawer's .profile-menu-* classes so both sides look identical.

const SUPPORT_EMAIL = "hello@requesta.app";

type Props = {
  onShare: () => void;
  /** The share fell back to copying the link — say so in the chip for 2s. */
  shareCopied: boolean;
  djName: string;
  currency?: string | null;
};

const IconDots = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="5" cy="12" r="2.2" />
    <circle cx="12" cy="12" r="2.2" />
    <circle cx="19" cy="12" r="2.2" />
  </svg>
);
const stroke = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
const IconShare = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <path d="M12 3v12" /><path d="M8 7l4-4 4 4" /><path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
  </svg>
);
const IconInfo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <circle cx="12" cy="12" r="9" /><path d="M12 11v5" /><path d="M12 8h.01" />
  </svg>
);
const IconFeedback = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" {...stroke} aria-hidden="true"><path d="M21 11.5a8.4 8.4 0 01-9 8.4 8.9 8.9 0 01-3.9-.9L3 20l1-4.1a8.4 8.4 0 01-1-4.4 8.5 8.5 0 019-8.4 8.5 8.5 0 019 8.4z" /></svg>
);
const IconTerms = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" {...stroke} aria-hidden="true"><rect x="6" y="3.5" width="12" height="17" rx="2" /><path d="M9.5 8.5h5" /><path d="M9.5 12h5" /><path d="M9.5 15.5h3" /></svg>
);
const IconHeadphones = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" {...stroke} aria-hidden="true"><path d="M4 14v-2a8 8 0 0 1 16 0v2" /><rect x="2.5" y="13.5" width="4.5" height="6.5" rx="2.2" /><rect x="17" y="13.5" width="4.5" height="6.5" rx="2.2" /></svg>
);
const IconGlobe = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18Z" />
  </svg>
);
const IconHelp = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" {...stroke} aria-hidden="true">
    <circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 .9-1 1.7" /><path d="M12 17h.01" />
  </svg>
);

const FanPageMenu: React.FC<Props> = ({ onShare, shareCopied, djName, currency }) => {
  const [open, setOpen] = useState(false);
  const [howOpen, setHowOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Tap-away and Escape close the menu.
  useEffect(() => {
    if (!open) return;
    const away = (e: Event) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("touchstart", away, { passive: true });
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("touchstart", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  const zero = formatAmountForCopy(0, currency);
  const contactHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(t("help.mailContactSubject", { djName }))}`;
  const feedbackHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(t("help.mailFeedbackSubject"))}`;

  return (
    <div className="fpm-wrap" ref={wrapRef}>
      {/* The share fell back to copying the link — a small label beside the
          chip for 2s (the chip itself is a 30px circle, no room for words). */}
      {shareCopied && <span className="fpm-copied" role="status">{t("fanMenu.copied")}</span>}
      <button
        type="button"
        className="fpm-chip"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("fanMenu.moreOptions")}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <IconDots />
      </button>

      {open && (
        <div className="fpm-menu" role="menu">
          <button
            type="button"
            className="fpm-row"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onShare();
            }}
          >
            <span className="fpm-ic"><IconShare /></span>
            {t("fanMenu.share")}
          </button>
          <button
            type="button"
            className="fpm-row"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              setHowOpen(true);
            }}
          >
            <span className="fpm-ic"><IconInfo /></span>
            {t("fanMenu.how")}
          </button>
          <button
            type="button"
            className="fpm-row"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              setHelpOpen(true);
            }}
          >
            <span className="fpm-ic"><IconHelp /></span>
            {t("fanMenu.help")}
          </button>
          {languagePickerAvailable() && (
            <button
              type="button"
              className="fpm-row"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                setLanguageOpen(true);
              }}
            >
              <span className="fpm-ic"><IconGlobe /></span>
              {t("fanMenu.language")}
            </button>
          )}
        </div>
      )}

      <BottomSheet open={howOpen} title={t("fanMenu.how")} icon={<IconInfo />} onClose={() => setHowOpen(false)} roomy>
        <ol className="fpm-how">
          <li>
            <strong>{t("fanHow.pickTitle")}</strong>
            <span>{t("fanHow.pickBody")}</span>
          </li>
          <li>
            <strong>{t("fanHow.bidTitle")}</strong>
            <span>{t("fanHow.bidBody", { djName })}</span>
          </li>
          <li>
            <strong>{t("fanHow.chargeTitle")}</strong>
            <span>{t("fanHow.chargeBody", { zero })}</span>
          </li>
        </ol>
      </BottomSheet>

      {/* The DJ side's chooser, row for row (MorePage "Help, terms & support"). */}
      <BottomSheet open={helpOpen} title={t("fanMenu.help")} onClose={() => setHelpOpen(false)}>
        <div className="profile-menu">
          <a className="profile-menu-row" href={feedbackHref} onClick={() => setHelpOpen(false)}>
            <span className="profile-menu-ic" aria-hidden="true"><IconFeedback /></span>
            <span className="profile-menu-label">{t("help.feedback")}</span>
            <FaChevronRight className="profile-menu-chev" aria-hidden />
          </a>
          <button
            type="button"
            className="profile-menu-row"
            onClick={() => {
              setHelpOpen(false);
              setTermsOpen(true);
            }}
          >
            <span className="profile-menu-ic" aria-hidden="true"><IconTerms /></span>
            <span className="profile-menu-label">{t("help.terms")}</span>
            <FaChevronRight className="profile-menu-chev" aria-hidden />
          </button>
          <a className="profile-menu-row" href={contactHref} onClick={() => setHelpOpen(false)}>
            <span className="profile-menu-ic" aria-hidden="true"><IconHeadphones /></span>
            <span className="profile-menu-label">{t("help.contact")}</span>
            <FaChevronRight className="profile-menu-chev" aria-hidden />
          </a>
        </div>
      </BottomSheet>

      <LanguageSheet open={languageOpen} onClose={() => setLanguageOpen(false)} />

      {/* Terms — the two legal documents; closing goes back one level. */}
      <BottomSheet
        open={termsOpen}
        title={t("help.terms")}
        onClose={() => {
          setTermsOpen(false);
          setHelpOpen(true);
        }}
      >
        <div className="profile-menu">
          <button type="button" className="profile-menu-row" onClick={() => navigate("/terms")}>
            <span className="profile-menu-ic" aria-hidden="true"><IconTerms /></span>
            <span className="profile-menu-label">{t("help.termsConditions")}</span>
            <FaChevronRight className="profile-menu-chev" aria-hidden />
          </button>
          <button type="button" className="profile-menu-row" onClick={() => navigate("/privacy")}>
            <span className="profile-menu-ic" aria-hidden="true"><IconTerms /></span>
            <span className="profile-menu-label">{t("help.privacy")}</span>
            <FaChevronRight className="profile-menu-chev" aria-hidden />
          </button>
        </div>
      </BottomSheet>
    </div>
  );
};

export default FanPageMenu;
